import React, { FC, ReactElement } from 'react'
import styles from './CreateWalletPassword.module.css'
import ContainedInput from '@/components/contained-input'
import classNames from 'classnames'

import { setPassword } from '@/utils/password'

import CreateWalletHeader from '../create-wallet-header'
import CreateWalletFooter from '../create-wallet-footer'
import CreateWalletTitle from '../create-wallet-title'

export const MIN_PASSWORD_LENGTH = 8

/**
 * Two modes:
 * - Create (`onPassword`): validate only and hand the password up. The parent
 *   persists the wallet after the backup check passes.
 * - Restore (`onNext`, `restore`): the phrase was already proven by typing it,
 *   so persist here and continue.
 */
const CreateWalletPassword: FC<{
	step: number
	totalSteps: number
	onNext?: (evt: any) => void
	onPassword?: (password: string) => void
	onBack: (evt: any) => void
	seed: string
	/** Prefill when the user comes back from a later step. */
	initialPassword?: string
	/** Restore: derive from the phrase bytes exactly as typed (legacy wallets). */
	exactBytes?: boolean
	restore?: boolean
}> = ({
	step,
	totalSteps,
	onNext,
	onPassword,
	onBack,
	seed,
	initialPassword,
	restore,
	exactBytes
}): ReactElement => {
	const [pass, setPass] = React.useState(initialPassword ?? '')
	const [confirmPass, setConfirmPass] = React.useState(initialPassword ?? '')
	const [error, setError] = React.useState('')
	const [loading, setLoading] = React.useState(false)

	const handleChangePass = React.useCallback((evt: any) => {
		setPass(evt.target.value)
		setError('')
	}, [])
	const handleChangeConfirmPass = React.useCallback((evt: any) => {
		setConfirmPass(evt.target.value)
		setError('')
	}, [])

	const handleSubmit = React.useCallback(
		async (evt: any) => {
			evt.preventDefault()
			evt.stopPropagation()

			if (loading) {
				return
			}

			if (pass.length < MIN_PASSWORD_LENGTH) {
				return setError(`password must be at least ${MIN_PASSWORD_LENGTH} characters`)
			}

			if (pass !== confirmPass) {
				return setError('passwords do not match')
			}

			if (onPassword) {
				onPassword(pass)
				return
			}

			setLoading(true)

			try {
				await setPassword(pass, seed, { exactBytes })
				onNext?.(evt)
			} catch (_e) {
				setError('An error occurred')
			} finally {
				setLoading(false)
			}
		},
		[loading, pass, confirmPass, seed, onNext, onPassword, exactBytes]
	)

	return (
		<form onSubmit={handleSubmit} className={styles.form}>
			<CreateWalletHeader
				step={step}
				total={totalSteps}
				nextDisabled={loading}
				onNext={handleSubmit}
				onBack={onBack}
			/>
			<div className={styles.root}>
				<CreateWalletTitle step={step} restore={restore} />
				<ContainedInput
					className={styles.input}
					placeholder="Password"
					type="password"
					name="new-password"
					autocomplete="new-password"
					autoFocus
					value={pass}
					error={!!error}
					onChange={handleChangePass}
				/>
				<ContainedInput
					className={styles.input}
					placeholder="Confirm Password"
					type="password"
					name="confirm-password"
					autocomplete="new-password"
					value={confirmPass}
					error={!!error}
					onChange={handleChangeConfirmPass}
				/>
				{error && (
					<p role="alert" className={classNames(styles.error, 'text-sm', 'regular')}>
						{error}
					</p>
				)}
			</div>
			<CreateWalletFooter
				step={step}
				nextDisabled={loading}
				onNext={handleSubmit}
				onBack={onBack}
				restore={restore}
				loading={loading}
			/>
		</form>
	)
}

export default CreateWalletPassword
